import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiTrash2, FiZap } from 'react-icons/fi';
import { theme } from 'theme/theme';
import {
  getEmailCategoryDescriptionFromContextValue,
  getEmailCategoryDisplayNameFromContextValue,
} from 'utils/emailCategoryContextUtils';
import { captureEvent } from 'utils/posthog';

import { OverflowMenu } from 'components/common/OverflowMenu';
import { InfoTooltip } from 'components/InfoTooltip';
import { DeterministicCategoryRuleRow } from 'components/settings/category-rules/DeterministicCategoryRuleRow';
import { AddCategoryWorkflowModal } from 'components/settings/guide-ai/AddCategoryWorkflowModal';
import { ANALYTICS_EVENTS } from 'constants/analytics-events';
import { COLOR_NAMED_WHITE, COLOR_TRANSPARENT } from 'constants/colors';
import { OPACITY_DISABLED, OPACITY_FULL } from 'constants/numbers';
import {
  CONTEXT_EXPLANATION_VIP_CONTACT_STARRED,
  CONTEXT_KEY_EMAIL_CATEGORY,
  CONTEXT_KEY_Q_AND_A,
  CONTEXT_SOURCE_AUTOGENERATED,
  CONTEXT_SOURCE_USER_EDITED,
  KEY_ENTER,
  KEY_ESCAPE,
  STRING_NONE,
} from 'constants/strings';
import { useCategoryRuleFromCategory } from 'contexts/CategoryRuleFromCategoryContext';

import { EmailCategoryContextDisplay } from './EmailCategoryContextDisplay';

const TRUNCATE_LENGTH = 100; // Characters before truncation

interface UserContext {
  contextId: string;
  contextKey: string;
  contextValue: string;
  source: string;
  priority?: number;
  explanation?: string;
  sourceThreadIds?: string[];
}

interface ContextSectionProps {
  title: string;
  contextKey: string | string[];
  contexts: UserContext[];
  addLabel: string;
  tooltipContent?: string;
  actionButton?: React.ReactNode;
  addingContextType: string | null;
  editingContextId: string | null;
  editContextValue: string;
  newContextValue: string;
  onAddContext: () => Promise<void>;
  onUpdateContext: () => Promise<void>;
  onDeleteContext: (contextId: string) => void;
  onNewContextValueChange: (value: string) => void;
  onAddingContextTypeChange: (type: string | null) => void;
  onEditingContextIdChange: (id: string | null) => void;
  onEditContextValueChange: (value: string) => void;
  /** If true, the section will be expanded by default (useful for deep-linking) */
  isInitiallyExpanded?: boolean;
  /** If true, a search input is shown at the top of the section body to filter items */
  searchable?: boolean;
}

interface ContextSectionBodyProps {
  filteredContexts: UserContext[];
  addType: string;
  addLabel: string;
  addingContextType: string | null;
  editingContextId: string | null;
  editContextValue: string;
  newContextValue: string;
  onAddContext: () => Promise<void>;
  onUpdateContext: () => Promise<void>;
  onDeleteContext: (contextId: string) => void;
  onNewContextValueChange: (value: string) => void;
  onAddingContextTypeChange: (type: string | null) => void;
  onEditingContextIdChange: (id: string | null) => void;
  onEditContextValueChange: (value: string) => void;
  searchable?: boolean;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
}

const ContextSectionBody: React.FC<ContextSectionBodyProps> = ({
  filteredContexts,
  addType,
  addLabel,
  addingContextType,
  editingContextId,
  editContextValue,
  newContextValue,
  onAddContext,
  onUpdateContext,
  onDeleteContext,
  onNewContextValueChange,
  onAddingContextTypeChange,
  onEditingContextIdChange,
  onEditContextValueChange,
  searchable,
  searchTerm,
  onSearchTermChange,
}) => {
  const { t } = useTranslation();
  return (
  <div style={{ padding: theme.spacing.md, display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
    {searchable && (
      <input
        type="text"
        value={searchTerm || ''}
        onChange={event => onSearchTermChange?.(event.target.value)}
        placeholder={t('settings.emailCategories.searchPlaceholder')}
        style={{
          padding: theme.spacing.sm,
          borderRadius: theme.borderRadius.md,
          border: `1px solid ${theme.colors.border.medium}`,
          fontSize: theme.typography.fontSize.sm,
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
    )}
    {filteredContexts.length > 0 ? ( // eslint-disable-line no-nested-ternary
      filteredContexts.map(context => (
        <ContextItem
          key={context.contextId}
          context={context}
          editingContextId={editingContextId}
          editContextValue={editContextValue}
          onUpdateContext={onUpdateContext}
          onEditingContextIdChange={onEditingContextIdChange}
          onEditContextValueChange={onEditContextValueChange}
          onDeleteContext={onDeleteContext}
        />
      ))
    ) : searchTerm ? (
      <div style={{ color: theme.colors.text.tertiary, fontSize: theme.typography.fontSize.sm, fontStyle: 'italic' }}>
        {t('settings.emailCategories.noSearchResults')}
      </div>
    ) : (
      <div style={{ color: theme.colors.text.tertiary, fontSize: theme.typography.fontSize.sm, fontStyle: 'italic' }} />
    )}
    {addingContextType === addType ? (
      <ContextAddInput
        newContextValue={newContextValue}
        onNewContextValueChange={onNewContextValueChange}
        onAddContext={onAddContext}
        onCancel={() => {
          onAddingContextTypeChange(null);
          onNewContextValueChange('');
        }}
      />
    ) : (
      <button
        onClick={() => {
          onAddingContextTypeChange(addType);
          onNewContextValueChange('');
        }}
        style={{
          alignSelf: 'flex-start',
          marginTop: theme.spacing.xs,
          background: COLOR_TRANSPARENT,
          border: `1px dashed ${theme.colors.border.medium}`,
          borderRadius: theme.borderRadius.md,
          padding: `${theme.spacing.xs} ${theme.spacing.md}`,
          color: theme.colors.text.secondary,
          cursor: 'pointer',
          fontSize: theme.typography.fontSize.sm,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
        }}
      >
        <span>+</span> {addLabel}
      </button>
    )}
  </div>
  );
};

interface ContextSectionHeaderProps {
  title: string;
  itemCount: number;
  isExpanded: boolean;
  tooltipContent?: string;
  actionButton?: React.ReactNode;
  onToggle: () => void;
}

const ContextSectionHeader: React.FC<ContextSectionHeaderProps> = ({
  title,
  itemCount,
  isExpanded,
  tooltipContent,
  actionButton,
  onToggle,
}) => (
  <div
    onClick={onToggle}
    style={{
      fontSize: theme.typography.fontSize.lg,
      color: theme.colors.text.primary,
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
      cursor: 'pointer',
      backgroundColor: theme.colors.background.paper,
      borderBottom: isExpanded ? `1px solid ${theme.colors.border.light}` : 'none',
      borderRadius: isExpanded ? `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0` : theme.borderRadius.md,
      transition: theme.transitions.fast,
    }}
  >
    <span
      style={{
        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: theme.transitions.fast,
        fontSize: theme.typography.fontSize.base,
        color: theme.colors.text.secondary,
      }}
    >
      ▶
    </span>
    <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{title}</span>
    <span
      style={{
        backgroundColor: theme.colors.greyscale[300],
        color: theme.colors.text.secondary,
        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        borderRadius: theme.borderRadius.full,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.medium,
      }}
    >
      {itemCount}
    </span>
    {tooltipContent && <InfoTooltip content={tooltipContent} />}
    {actionButton && <div onClick={event => event.stopPropagation()}>{actionButton}</div>}
  </div>
);

export const ContextSection: React.FC<ContextSectionProps> = ({
  title,
  contextKey,
  contexts,
  addLabel,
  tooltipContent,
  actionButton,
  addingContextType,
  editingContextId,
  editContextValue,
  newContextValue,
  onAddContext,
  onUpdateContext,
  onDeleteContext,
  onNewContextValueChange,
  onAddingContextTypeChange,
  onEditingContextIdChange,
  onEditContextValueChange,
  isInitiallyExpanded = false,
  searchable = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(Boolean(isInitiallyExpanded));
  const [searchTerm, setSearchTerm] = useState('');
  const keys = Array.isArray(contextKey) ? contextKey : [contextKey];
  const filteredContexts = contexts.filter(ctx => keys.includes(ctx.contextKey));
  const addType = keys[0];
  const itemCount = filteredContexts.length;

  const searchFilteredContexts =
    searchable && searchTerm
      ? filteredContexts.filter(ctx => {
          const term = searchTerm.toLowerCase();
          if (ctx.contextKey === CONTEXT_KEY_EMAIL_CATEGORY) {
            const name = getEmailCategoryDisplayNameFromContextValue(ctx.contextValue).toLowerCase();
            const desc = (getEmailCategoryDescriptionFromContextValue(ctx.contextValue) || '').toLowerCase();
            return name.includes(term) || desc.includes(term) || ctx.contextId.toLowerCase().includes(term);
          }
          return ctx.contextValue.toLowerCase().includes(term);
        })
      : filteredContexts;

  React.useEffect(() => {
    if (isInitiallyExpanded) {
      setIsExpanded(true);
    }
  }, [isInitiallyExpanded]);

  return (
    <div
      style={{
        marginBottom: theme.spacing.lg,
        border: `1px solid ${theme.colors.border.light}`,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.background.paper,
      }}
    >
      <ContextSectionHeader
        title={title}
        itemCount={itemCount}
        isExpanded={isExpanded}
        tooltipContent={tooltipContent}
        actionButton={actionButton}
        onToggle={() => setIsExpanded(!isExpanded)}
      />
      {isExpanded && (
        <ContextSectionBody
          filteredContexts={searchFilteredContexts}
          addType={addType}
          addLabel={addLabel}
          addingContextType={addingContextType}
          editingContextId={editingContextId}
          editContextValue={editContextValue}
          newContextValue={newContextValue}
          onAddContext={onAddContext}
          onUpdateContext={onUpdateContext}
          onDeleteContext={onDeleteContext}
          onNewContextValueChange={onNewContextValueChange}
          onAddingContextTypeChange={onAddingContextTypeChange}
          onEditingContextIdChange={onEditingContextIdChange}
          onEditContextValueChange={onEditContextValueChange}
          searchable={searchable}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
        />
      )}
    </div>
  );
};

interface ContextItemEditFormProps {
  editContextValue: string;
  onUpdateContext: () => Promise<void>;
  onEditContextValueChange: (value: string) => void;
  onCancelEdit: () => void;
}

const ContextItemEditForm: React.FC<ContextItemEditFormProps> = ({
  editContextValue,
  onUpdateContext,
  onEditContextValueChange,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing.sm,
        backgroundColor: theme.colors.background.subtle,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.colors.border.light}`,
      }}
    >
      <div style={{ display: 'flex', flex: 1, gap: theme.spacing.sm }}>
        <input
          type="text"
          value={editContextValue}
          onChange={event => onEditContextValueChange(event.target.value)}
          style={{
            flex: 1,
            padding: theme.spacing.xs,
            borderRadius: theme.borderRadius.sm,
            border: `1px solid ${theme.colors.border.medium}`,
          }}
        />
        <button
          onClick={onUpdateContext}
          style={{
            cursor: 'pointer',
            color: theme.colors.primary.main,
            border: STRING_NONE,
            background: STRING_NONE,
          }}
        >
          {t('common.save')}
        </button>
        <button
          onClick={onCancelEdit}
          style={{
            cursor: 'pointer',
            color: theme.colors.text.secondary,
            border: STRING_NONE,
            background: STRING_NONE,
          }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};

interface ContextItemProps {
  context: UserContext;
  editingContextId: string | null;
  editContextValue: string;
  onUpdateContext: () => Promise<void>;
  onEditingContextIdChange: (id: string | null) => void;
  onEditContextValueChange: (value: string) => void;
  onDeleteContext: (contextId: string) => void;
}

const ContextItem: React.FC<ContextItemProps> = ({
  context,
  editingContextId,
  editContextValue,
  onUpdateContext,
  onEditingContextIdChange,
  onEditContextValueChange,
  onDeleteContext,
}) => {
  const { t } = useTranslation();
  const categoryRuleFromCategory = useCategoryRuleFromCategory();
  const [showRulesAccordion, setShowRulesAccordion] = useState(false);
  const [showAddWorkflow, setShowAddWorkflow] = useState(false);
  const categoryDisplayNameForRule =
    context.contextKey === CONTEXT_KEY_EMAIL_CATEGORY
      ? getEmailCategoryDisplayNameFromContextValue(context.contextValue)
      : '';
  const canAddWorkflow = context.contextKey === CONTEXT_KEY_EMAIL_CATEGORY && Boolean(categoryDisplayNameForRule);
  const isEmailCategory = context.contextKey === CONTEXT_KEY_EMAIL_CATEGORY && categoryRuleFromCategory !== null;
  const matchingRules = isEmailCategory
    ? (categoryRuleFromCategory?.rules ?? []).filter(rule => rule.categoryName === categoryDisplayNameForRule)
    : [];
  const ruleCount = matchingRules.length;

  if (editingContextId === context.contextId) {
    return (
      <ContextItemEditForm
        editContextValue={editContextValue}
        onUpdateContext={onUpdateContext}
        onEditContextValueChange={onEditContextValueChange}
        onCancelEdit={() => onEditingContextIdChange(null)}
      />
    );
  }

  return (
    // data-category-name lets a deep link (inbox ⚙️ → settings) scroll straight
    // to a specific category's row. Only set for email categories.
    <div data-category-name={categoryDisplayNameForRule || undefined}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing.sm,
          backgroundColor: theme.colors.background.subtle,
          borderRadius: showRulesAccordion
            ? `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`
            : theme.borderRadius.md,
          border: `1px solid ${theme.colors.border.light}`,
          borderBottom: showRulesAccordion ? 'none' : `1px solid ${theme.colors.border.light}`,
        }}
      >
        <ContextItemContent context={context} />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing.sm,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {isEmailCategory && ruleCount > 0 && (
            <button
              type="button"
              onClick={() => setShowRulesAccordion(prev => !prev)}
              style={{
                cursor: 'pointer',
                color: theme.colors.primary.main,
                border: STRING_NONE,
                background: STRING_NONE,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {t('settings.emailCategories.rulesCount', { count: ruleCount })}
            </button>
          )}
          {isEmailCategory && ruleCount === 0 && (
            <button
              type="button"
              title={t('settings.emailCategories.addMatchingRuleTooltip')}
              disabled={!categoryDisplayNameForRule}
              onClick={() => {
                if (!categoryRuleFromCategory || !categoryDisplayNameForRule) {
                  return;
                }
                captureEvent(ANALYTICS_EVENTS.CONTEXT_ADD_MATCHING_RULE_CLICKED, { context_key: context.contextKey });
                categoryRuleFromCategory.openAddRuleForCategoryDisplayName(categoryDisplayNameForRule);
              }}
              style={{
                cursor: categoryDisplayNameForRule ? 'pointer' : 'not-allowed',
                color: theme.colors.primary.main,
                border: STRING_NONE,
                background: STRING_NONE,
                fontSize: theme.typography.fontSize.sm,
                opacity: categoryDisplayNameForRule ? OPACITY_FULL : OPACITY_DISABLED,
              }}
            >
              {t('settings.emailCategories.addMatchingRule')}
            </button>
          )}
          <button
            type="button"
            title={t('common.edit')}
            aria-label={t('common.edit')}
            onClick={() => {
              captureEvent(ANALYTICS_EVENTS.CONTEXT_EDIT_CLICKED, { context_key: context.contextKey });
              onEditingContextIdChange(context.contextId);
              onEditContextValueChange(context.contextValue);
            }}
            style={{
              cursor: 'pointer',
              color: theme.colors.primary.main,
              border: STRING_NONE,
              background: STRING_NONE,
              fontSize: theme.typography.fontSize.sm,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FiEdit2 size={14} />
          </button>
          <button
            type="button"
            title={t('common.delete')}
            aria-label={t('common.delete')}
            onClick={() => {
              captureEvent(ANALYTICS_EVENTS.CONTEXT_DELETED, { context_key: context.contextKey });
              onDeleteContext(context.contextId);
            }}
            style={{
              cursor: 'pointer',
              color: theme.colors.accent.error,
              border: STRING_NONE,
              background: STRING_NONE,
              fontSize: theme.typography.fontSize.sm,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FiTrash2 size={14} />
          </button>
          {canAddWorkflow && (
            <OverflowMenu
              aria-label={t('settings.categoryWorkflows.menuLabel')}
              items={[
                {
                  key: 'add-workflow',
                  label: t('settings.categoryWorkflows.addWorkflow'),
                  icon: <FiZap size={14} />,
                  onClick: () => {
                    captureEvent(ANALYTICS_EVENTS.CONTEXT_ADD_WORKFLOW_CLICKED, {
                      context_key: context.contextKey,
                    });
                    setShowAddWorkflow(true);
                  },
                },
              ]}
            />
          )}
        </div>
      </div>
      {showAddWorkflow && (
        <AddCategoryWorkflowModal
          categoryId={context.contextId}
          categoryName={categoryDisplayNameForRule}
          onClose={() => setShowAddWorkflow(false)}
        />
      )}
      {showRulesAccordion && matchingRules.length > 0 && (
        <div
          style={{
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.background.paper,
            borderRadius: `0 0 ${theme.borderRadius.md} ${theme.borderRadius.md}`,
            border: `1px solid ${theme.colors.border.light}`,
            borderTop: 'none',
          }}
        >
          {matchingRules.map(matchingRule => (
            <DeterministicCategoryRuleRow
              key={matchingRule.id}
              rule={matchingRule}
              onToggleEnabled={(id, nextEnabled) => categoryRuleFromCategory?.onToggleEnabled(id, nextEnabled)}
              onDelete={async id => {
                  await categoryRuleFromCategory?.onDeleteRule(id);
                }
              }
              onEditComposite={ruleToEdit => categoryRuleFromCategory?.onEditRule(ruleToEdit)}
            />
          ))}
          <button
            type="button"
            onClick={() => {
              if (categoryRuleFromCategory && categoryDisplayNameForRule) {
                captureEvent(ANALYTICS_EVENTS.CONTEXT_ADD_MATCHING_RULE_CLICKED, { context_key: context.contextKey });
                categoryRuleFromCategory.openAddRuleForCategoryDisplayName(categoryDisplayNameForRule);
              }
            }}
            style={{
              marginTop: theme.spacing.xs,
              background: COLOR_TRANSPARENT,
              border: STRING_NONE,
              color: theme.colors.primary.main,
              cursor: 'pointer',
              fontSize: theme.typography.fontSize.sm,
            }}
          >
            {t('settings.emailCategories.addMatchingRuleWithPrefix')}
          </button>
        </div>
      )}
    </div>
  );
};

function parseQAndA(contextValue: string): { question: string; answer: string } {
  const parts = contextValue.split(' | ');
  const question =
    parts
      .find(part => part.startsWith('Q:'))
      ?.replace('Q:', '')
      .trim() || '';
  const answer =
    parts
      .find(part => part.startsWith('A:'))
      ?.replace('A:', '')
      .trim() || '';
  return { question, answer };
}

function translateExplanation(
  explanation: string,
  tFunc: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (!explanation.includes(':')) {
    return explanation;
  }
  const [key, ...params] = explanation.split(':');
  try {
    if (key === CONTEXT_EXPLANATION_VIP_CONTACT_STARRED && params[0]) {
      const count = parseInt(params[0], 10);
      return tFunc('settings.contextExplanations.vipContactStarredExplanation', {
        count,
        plural: count > 1 ? 's' : '',
      });
    }
    const normalizedKey = key
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join('');
    return tFunc(`settings.contextExplanations.${normalizedKey}`, {
      reason: params.join(':').trim(),
      defaultValue: explanation,
    });
  } catch {
    return explanation;
  }
}

interface ContextItemContentProps {
  context: UserContext;
}

const ContextItemContent: React.FC<ContextItemContentProps> = ({ context }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const isEmailCategory = context.contextKey === CONTEXT_KEY_EMAIL_CATEGORY;
  const shouldTruncate = !isEmailCategory && context.contextValue.length > TRUNCATE_LENGTH;
  const displayValue =
    shouldTruncate && !isExpanded ? `${context.contextValue.substring(0, TRUNCATE_LENGTH)}...` : context.contextValue;

  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
          {context.contextKey === CONTEXT_KEY_Q_AND_A && context.contextValue.includes('|') ? ( // eslint-disable-line no-nested-ternary
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs, width: '100%' }}>
              {(() => {
                const { question, answer } = parseQAndA(context.contextValue);
                return (
                  <>
                    <div style={{ color: theme.colors.text.primary, fontWeight: theme.typography.fontWeight.medium }}>
                      {t('settings.context.question')}: {question}
                    </div>
                    <div style={{ color: theme.colors.text.secondary, marginLeft: theme.spacing.md }}>
                      {t('settings.context.answer')}: {answer}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : isEmailCategory ? (
            <EmailCategoryContextDisplay contextId={context.contextId} contextValue={context.contextValue} />
          ) : (
            <span style={{ color: theme.colors.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {displayValue}
              {shouldTruncate && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  style={{
                    marginLeft: theme.spacing.xs,
                    color: theme.colors.primary.main,
                    background: STRING_NONE,
                    border: STRING_NONE,
                    cursor: 'pointer',
                    fontSize: theme.typography.fontSize.sm,
                    padding: 0,
                  }}
                >
                  {isExpanded ? t('common.showLess') : t('common.showMore')}
                </button>
              )}
            </span>
          )}
          {(context.source === CONTEXT_SOURCE_AUTOGENERATED || context.source === CONTEXT_SOURCE_USER_EDITED) && (
            <span
              style={{
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.text.tertiary,
                backgroundColor: theme.colors.background.paper,
                padding: '2px 6px',
                borderRadius: theme.borderRadius.sm,
                border: `1px solid ${theme.colors.border.light}`,
              }}
            >
              {context.source === CONTEXT_SOURCE_USER_EDITED ? t('common.custom') : t('common.auto')}
            </span>
          )}
        </div>
        {context.explanation && (
          <span
            style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, fontStyle: 'italic' }}
          >
            {translateExplanation(context.explanation, t)}
          </span>
        )}
        {context.sourceThreadIds && context.sourceThreadIds.length > 0 && (
          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.primary.main, cursor: 'pointer' }}>
            <a
              href={`/email/${context.sourceThreadIds[0]}`}
              style={{ color: theme.colors.primary.main, textDecoration: 'none' }}
              title={t('guideAi.viewSourceThread', { count: context.sourceThreadIds.length })}
            >
              {t('guideAi.sourceThreads', { count: context.sourceThreadIds.length })}
            </a>
          </span>
        )}
      </div>
    </div>
  );
};

interface ContextAddInputProps {
  newContextValue: string;
  onNewContextValueChange: (value: string) => void;
  onAddContext: () => Promise<void>;
  onCancel: () => void;
}

const ContextAddInput: React.FC<ContextAddInputProps> = ({
  newContextValue,
  onNewContextValueChange,
  onAddContext,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.xs }}>
      <input
        type="text"
        value={newContextValue}
        onChange={event => onNewContextValueChange(event.target.value)}
        placeholder={t('settings.addContext.placeholder')}
        autoFocus
        style={{
          flex: 1,
          padding: theme.spacing.sm,
          borderRadius: theme.borderRadius.md,
          border: `1px solid ${theme.colors.primary.main}`,
        }}
        onKeyDown={event => {
          if (event.key === KEY_ENTER) {
            onAddContext();
          }
          if (event.key === KEY_ESCAPE) {
            onCancel();
          }
        }}
      />
      <button
        onClick={onAddContext}
        disabled={!newContextValue.trim()}
        style={{
          padding: `${theme.spacing.xs} ${theme.spacing.md}`,
          backgroundColor: theme.colors.primary.main,
          color: COLOR_NAMED_WHITE,
          border: STRING_NONE,
          borderRadius: theme.borderRadius.md,
          cursor: newContextValue.trim() ? 'pointer' : 'not-allowed',
          opacity: newContextValue.trim() ? OPACITY_FULL : OPACITY_DISABLED,
        }}
      >
        {t('common.add')}
      </button>
      <button
        onClick={onCancel}
        style={{
          padding: `${theme.spacing.xs} ${theme.spacing.md}`,
          backgroundColor: COLOR_TRANSPARENT,
          color: theme.colors.text.secondary,
          border: STRING_NONE,
          cursor: 'pointer',
        }}
      >
        {t('common.cancel')}
      </button>
    </div>
  );
};
